import { selectBuCode } from '@repo/api-adapters/selectors/customer-assets.selectors';
import { selectDeliveryContact } from '@repo/api-adapters/selectors/customer.selectors';
import type { SelectedServices } from '@repo/api-adapters/selectors/services.selectors';
import { type OrderAsset } from '@repo/types/order';
import { type TimeWindowsPayload } from '@repo/types/time-windows';

import {
    findDeliveryServiceByServiceId,
    findProvidedServiceByServiceId,
    getServiceTransportTimeWindows,
} from './services.api.utils';

interface ReferenceItem {
    itemId?: string | undefined;
    itemType?: string | undefined;
    quantityType?: string | undefined;
    quantity?: number | undefined;
    retailItemName?: string | undefined;
    seqNo?: number | undefined;
}

const mapReferenceItems = (referenceItems: ReferenceItem[] | undefined) =>
    referenceItems
        ?.filter((item): item is typeof item & { itemType: string; itemId: string } =>
            Boolean(item.itemType && item.itemId)
        )
        .map((item) => ({
            itemId: `${item.itemType}${item.itemId}`,
            itemName: item.retailItemName,
            lineId: item.seqNo,
            qty: item.quantity,
            qtyType: item.quantityType,
        }));

interface TransportTimeWindowSource {
    timeWindowId: string;
    fromDateTimeUtc: string;
    toDateTimeUtc: string;
}

export const buildTransportTimeWindow = (deliveryNo: string, selectedTimeWindow: TransportTimeWindowSource) => {
    return {
        deliveryNo,
        transportTimewindow: {
            transportTimewindowId: selectedTimeWindow.timeWindowId,
            start: selectedTimeWindow.fromDateTimeUtc,
            end: selectedTimeWindow.toDateTimeUtc,
            serviceIdentifier: deliveryNo,
        },
    };
};

interface BuildTimeWindowsPayloadParams {
    orderData: OrderAsset;
    services: SelectedServices;
    serviceId: string;
    userId: TimeWindowsPayload['userId'];
    locale: TimeWindowsPayload['locale'];
    fromDate: string;
    deliveryOverride?: ReturnType<typeof buildTransportTimeWindow>;
}

export const buildTimeWindowsPayload = ({
    orderData,
    services,
    serviceId,
    userId,
    locale,
    fromDate,
    deliveryOverride,
}: BuildTimeWindowsPayloadParams): TimeWindowsPayload | undefined => {
    const deliveryContact = selectDeliveryContact(orderData);

    const basePayload = {
        businessUnit: { buCode: selectBuCode(orderData) },
        shippingAddress: {
            zipCode: deliveryContact?.zipCode,
            countryCode: deliveryContact?.countryCode,
        },
        userId,
        fromDate,
        integrator: 'ISOM',
        locale,
    };

    // Try delivery service first
    const matchedDeliveryService = findDeliveryServiceByServiceId(orderData, serviceId);
    if (matchedDeliveryService?.deliveryNo && matchedDeliveryService.serviceId) {
        const selectedService = services.find((s) => s.serviceId === matchedDeliveryService.serviceId);

        return {
            ...basePayload,
            order: { orderNo: orderData.assetDetails.orderNo ?? '' },
            items: mapReferenceItems(selectedService?.referenceItems),
            delivery: {
                deliveryId: matchedDeliveryService.deliveryNo,
                serviceItemId: matchedDeliveryService.serviceId,
            },
        };
    }

    // Try provided service
    const matchedProvidedService = findProvidedServiceByServiceId(orderData, serviceId);
    if (matchedProvidedService?.serviceProviderId && matchedProvidedService.serviceProductId) {
        const selectedService = services.find((s) => s.serviceId === matchedProvidedService.serviceId);

        const articles =
            selectedService?.referenceItems
                ?.filter((item) => Boolean(item.itemType && item.itemId))
                .map((item) => ({
                    itemNo: `${item.itemType}${item.itemId}`,
                    quantity: item.quantity ?? 1,
                })) ?? [];

        return {
            ...basePayload,
            items: mapReferenceItems(selectedService?.referenceItems),
            providedService: {
                transportTimewindows: getServiceTransportTimeWindows(orderData, serviceId, deliveryOverride),
                serviceProviderId: matchedProvidedService.serviceProviderId,
                capacityNeeded: matchedProvidedService.capacityValue ?? 0,
                capacityUnit: matchedProvidedService.capacityUnit ?? '',
                serviceItemNo: matchedProvidedService.serviceId ?? '',
                serviceProductId: matchedProvidedService.serviceProductId,
                articles,
            },
        };
    }

    return undefined;
};
